export const AUTH_FORM_COPY = {
  termsMustBeAccepted: 'Terms must be accepted.',
  invalidEmail: 'Enter a valid email address.',
  firstNameRequired: 'First name is required.',
  lastNameRequired: 'Last name is required.',
  passwordRequirements: 'Password does not meet the requirements.',
  passwordRequired: 'Password is required.',
  termsInline: 'terms and privacy policies must be read and checked before sign up',
} as const;

const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailAddress(value: string): boolean {
  return EMAIL_ADDRESS_PATTERN.test(value.trim());
}

export type PasswordRuleChecks = {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
};

export function getPasswordRuleChecks(password: string): PasswordRuleChecks {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
}

export function isSignupPasswordValid(password: string): boolean {
  const checks = getPasswordRuleChecks(password);
  return checks.minLength && checks.hasUppercase && checks.hasLowercase && checks.hasNumber;
}

export type SignUpFormInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  agreedToTerms: boolean;
};

export type SignUpFormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
};

export type SignUpFormValidation = {
  ok: boolean;
  errors: SignUpFormErrors;
  termsInvalid: boolean;
  /** Terms toast wins when the box is unchecked. Otherwise the first field error. */
  toast: string | null;
};

export function validateSignUpForm(input: SignUpFormInput): SignUpFormValidation {
  const errors: SignUpFormErrors = {};
  if (!input.firstName.trim()) {
    errors.firstName = AUTH_FORM_COPY.firstNameRequired;
  }
  if (!input.lastName.trim()) {
    errors.lastName = AUTH_FORM_COPY.lastNameRequired;
  }
  if (!isValidEmailAddress(input.email)) {
    errors.email = AUTH_FORM_COPY.invalidEmail;
  }
  if (!isSignupPasswordValid(input.password)) {
    errors.password = AUTH_FORM_COPY.passwordRequirements;
  }

  const termsInvalid = !input.agreedToTerms;
  const fieldToast = errors.firstName || errors.lastName || errors.email || errors.password || null;

  return {
    ok: !termsInvalid && fieldToast == null,
    errors,
    termsInvalid,
    toast: termsInvalid ? AUTH_FORM_COPY.termsMustBeAccepted : fieldToast,
  };
}

export type SignInFormValidation = {
  ok: boolean;
  emailError?: string;
  passwordError?: string;
  toast: string | null;
};

export function validateSignInForm(input: {
  email: string;
  password: string;
}): SignInFormValidation {
  const emailError = isValidEmailAddress(input.email) ? undefined : AUTH_FORM_COPY.invalidEmail;
  const passwordError = input.password.length > 0 ? undefined : AUTH_FORM_COPY.passwordRequired;
  const toast = emailError || passwordError || null;
  return {
    ok: toast == null,
    emailError,
    passwordError,
    toast,
  };
}
